import {
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "mantine-react-table";
import {
  ICommonDataTableProps,
  IDataTableColumnProps,
} from "./DataTable.types";
import { BaseDataTable } from "./BaseDataTable";
import React from "react";

export interface IDynamicFetchFilter {
  id: string;
  value: unknown;
}

export interface IDynamicFetchSort {
  id: string;
  desc: boolean;
}

export interface IDynamicFetchParameters {
  pageIndex: number;
  pageSize: number;
  sort: IDynamicFetchSort[];
  globalFilter?: string;
  filters?: IDynamicFetchFilter[] | null;
}

export interface IDynamicDataTableProps<
  TFetchResponse extends Record<string, any>,
  TData extends Record<string, any>,
> extends ICommonDataTableProps<TData> {
  initialSorting?: IDynamicFetchSort[];
  initialFilters?: IDynamicFetchFilter[];
  fetchData: (parameters: IDynamicFetchParameters) => Promise<TFetchResponse>;
  getCount: (response: TFetchResponse) => number;
  getData: (response: TFetchResponse) => TData[];
  columnSelector?: (
    response: TFetchResponse,
  ) => IDataTableColumnProps<TData>[] | undefined;
  emptyMessage?: ReactNode;
}

export interface IDynamicDataTable {
  refresh(): void;
}

export const DynamicDataTable = <
  TFetchResponse extends Record<string, any>,
  TData extends Record<string, any>,
>({
  columns,
  sortable,
  initialSorting,
  filterable,
  initialFilters,
  hasPagination,
  showFooter,
  fetchData,
  getCount,
  getData,
  controlRef,
  columnSelector,
  emptyMessage,
  mantineTableProps,
  mantineTableBodyRowProps,
}: IDynamicDataTableProps<TFetchResponse, TData> & {
  controlRef?: Ref<IDynamicDataTable>;
}) => {
  //data and fetching state
  const [data, setData] = useState<TData[] | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [overrideColumns, setOverrideColumns] = useState<
    IDataTableColumnProps<TData>[] | undefined
  >(undefined);

  //table state
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    initialFilters || [],
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>(
    initialSorting || [],
  );
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const updateData = useCallback(
    async (
      firstLoad: boolean = false,
      updatePagination: MRT_PaginationState,
      updateSorting: MRT_SortingState,
      updateGlobalFilter: string,
      updateColumnFilters: MRT_ColumnFiltersState,
    ) => {
      if (firstLoad) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      try {
        const response = await fetchData({
          pageIndex: updatePagination.pageIndex,
          pageSize: updatePagination.pageSize,
          sort: updateSorting,
          globalFilter: filterable === true ? updateGlobalFilter : undefined,
          filters:
            filterable === true
              ? updateColumnFilters.map((f) => ({ id: f.id, value: f.value }))
              : undefined,
        });
        setOverrideColumns(columnSelector?.(response));
        setData(getData(response));
        setRowCount(getCount(response));
      } catch (error) {
        setIsError(true);
        console.error(error);
        return;
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    },
    [columnSelector, fetchData, filterable, getCount, getData],
  );

  useImperativeHandle(controlRef, () => ({
    refresh() {
      if (data && data.length === 0) {
        setData(null);
      }
      updateData(false, pagination, sorting, globalFilter, columnFilters);
    },
  }));

  const hasData = !data;
  //if you want to avoid useEffect, look at the React Query example instead
  useEffect(() => {
    updateData(hasData, pagination, sorting, globalFilter, columnFilters);
  }, [pagination, sorting, globalFilter, columnFilters]);

  if (data && data.length === 0 && emptyMessage) {
    return <>{emptyMessage}</>;
  }

  return (
    <BaseDataTable<TData>
      data={data || []}
      columns={overrideColumns || columns}
      sortable={sortable}
      filterable={filterable}
      hasPagination={hasPagination}
      showFooter={showFooter}
      tableProps={{
        pageCount: Math.ceil(rowCount / pagination.pageSize),
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        rowCount: rowCount,
        state: {
          columnFilters,
          globalFilter,
          pagination,
          sorting,
        },
      }}
      mantineTableProps={mantineTableProps}
      mantineTableBodyRowProps={mantineTableBodyRowProps}
      isLoading={isLoading}
      isError={isError}
      isRefetching={isRefetching}
    />
  );
};
